import React, { FC } from 'react';

import {
  Box,
  Text,
  Flex,
  Stack,
  Input,
  Button,
  useToast,
  Textarea,
  SimpleGrid,
  Heading,
} from '@chakra-ui/react';

import { FormLabel } from '@chakra-ui/form-control';

import { washHub } from 'store';

import { useSKU } from 'hooks';

import { rejectOrder } from 'api/order';
import { useMutation, useQueryClient } from 'react-query';

import { Order } from 'types/order';

const labelProps = {
  fontSize: '12px',
  color: '#afafaf',
};

const px = { base: '20px', md: '100px' };

const DayOfTheWeekMap: { [key: string]: string } = {
  Monday: 'Mon',
  Tuesday: 'Tue',
  Wednesday: 'Wed',
  Thursday: 'Thu',
  Friday: 'Fri',
  Saturday: 'Sat',
  Sunday: 'Sun',
};

type Props = {
  order: Order | null;
  onClose: () => void;
  onAccept: () => void;
};

export const CustomerRequest: FC<Props> = ({ order, onClose, onAccept }) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const { data: sku = [] } = useSKU('package');

  const washHubId = washHub.selectors.useSelectedWashHubId();

  const { mutate, isLoading } = useMutation(
    () => rejectOrder(order?._id || ''),
    {
      onSuccess: () => {
        onClose();

        queryClient.invalidateQueries({
          queryKey: [`${washHubId}-fetch-orders`],
        });

        toast({
          status: 'success',
          title: 'Order Rejected Successfully',
          isClosable: true,
          duration: 3000,
        });
      },
    },
  );

  const _site = order?.site;

  const isDaily = _site?.deliveryFrequency === 'Daily';
  const _deliveryDayOfTheWeek = _site?.deliveryDayOfTheWeek;

  let deliveryDayOfTheWeek = '';

  if (isDaily) {
    deliveryDayOfTheWeek = '';
  } else if (Array.isArray(_deliveryDayOfTheWeek)) {
    if (_deliveryDayOfTheWeek?.length === 1) {
      deliveryDayOfTheWeek = _deliveryDayOfTheWeek?.[0];
    } else {
      deliveryDayOfTheWeek = _deliveryDayOfTheWeek
        ?.map((day) => DayOfTheWeekMap[day])
        ?.join(', ');
    }
  }

  const frequency = _site?.deliveryFrequency;

  let deliveryFrequency = frequency ? `(${frequency})` : '';

  if (isDaily) {
    deliveryFrequency = frequency || '';
  }

  const details = order?.details;

  return (
    <Flex direction="column">
      <Flex
        shadow="lg"
        w="100%"
        py="30px"
        px={px}
        align="center"
        justify="space-between">
        <Heading fontSize="24px" fontWeight="600">
          Customer Request
        </Heading>
      </Flex>

      <Flex direction="column" py="30px" px={px}>
        <Flex>
          <Flex direction="column" flex={1} maxW="300px">
            <FormLabel {...labelProps} textTransform="capitalize">
              Site
            </FormLabel>
            <Input value={order?.site?.name || ''} isReadOnly />
          </Flex>
          <Box h="20px" w="20px" />
          <Flex direction="column" flex={1} maxW="300px">
            <FormLabel {...labelProps} textTransform="capitalize">
              Delivery Day of the week
            </FormLabel>
            <Input
              value={`${deliveryDayOfTheWeek} ${deliveryFrequency}` || '0'}
              isReadOnly
            />
          </Flex>
        </Flex>
        <Flex mt="5" direction="column" flex={1}>
          <FormLabel {...labelProps} textTransform="capitalize">
            Notes, Questions, Feedback
          </FormLabel>
          <Textarea value={order?.notes || ''} isReadOnly />
        </Flex>
        <SimpleGrid
          mt="5"
          mb="20px"
          flexWrap="wrap"
          columns={[1, 1, 3]}
          spacing={4}>
          {details?.map((d: any) => {
            const _sku = sku.find((s) => s._id === d?.sku);

            return (
              <Flex
                key={JSON.stringify(d)}
                direction="column"
                maxW="300px"
                w="100%">
                <FormLabel {...labelProps} textTransform="capitalize">
                  Cases of {_sku?.name || ''}
                </FormLabel>
                <Input value={d?.casesCount || '0'} isReadOnly />
              </Flex>
            );
          })}
        </SimpleGrid>
        <Stack mt="5" isInline justify="center">
          <Button
            isLoading={isLoading}
            onClick={() => mutate()}
            bg="red.500"
            _hover={{ bg: 'red.600' }}
            _active={{ bg: 'red.800' }}>
            Reject Request
          </Button>
          <Button isDisabled={isLoading} onClick={onAccept}>
            Accept Request
          </Button>
        </Stack>
      </Flex>
    </Flex>
  );
};
